import React, { ChangeEvent, useMemo } from 'react'
import { useFormik } from 'formik'
import { Button, Grid, CircularProgress, Box, FormControlLabel, Switch } from '@mui/material'
import { Checkbox, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useNotification } from 'hooks'
import { useCreatePartnerMutation, usePartnerQuery, useUpdatePartnerMutation } from '../../api'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import sl from '../../partners.modules.scss'
import { PartnersTableItem, EditPartnerModalProps } from '../../interfaces'
import { partnersValidationSchema } from '../helpers'
import { is } from 'immer/dist/internal'

const EditPartnerModal: React.FC<EditPartnerModalProps> = ({
  isModalOpen = false,
  onModalClose,
  partnerId,
  onSuccess
}) => {
  const { showNotification } = useNotification()

  const shouldRefreshData = useMemo(() => isModalOpen === true, [isModalOpen])

  const { data: partner, isLoading } = usePartnerQuery(
    showNotification,
    partnerId,
    shouldRefreshData,
    { enabled: isModalOpen }
  )

  const updatePartnerMutation = useUpdatePartnerMutation(showNotification)
  const createPartnerMutation = useCreatePartnerMutation(showNotification)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: partner?.firstName || '',
      lastName: partner?.lastName || '',
      email: partner?.email || '',
      phone: partner?.phone || '',
      emailNotProvided: !partner?.email || false,
      phoneNotProvided: !partner?.phone || false,
      isEnabled: partner?.isEnabled ?? true,
      details: partner?.details || ''
    },
    validationSchema: partnersValidationSchema,
    onSubmit: async values => {
      const updatedValues: Partial<PartnersTableItem> = {
        ...values
      }
      if (!partnerId) {
        await createPartnerMutation.mutateAsync(updatedValues)
      } else {
        updatedValues.id = partnerId
        await updatePartnerMutation.mutateAsync({ partnerId, updatedData: updatedValues })
      }
      onSuccess()
      onModalClose()
      formik.resetForm()
    }
  })

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={onModalClose}
      modalTitle={partnerId ? 'Edit Partner' : 'Create Partner'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        {isLoading ? (
          <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
        ) : (
          <>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="firstName"
                label="First Name"
                width="100%"
                disabled={!!partnerId}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="lastName"
                label="Last Name"
                width="100%"
                disabled={!!partnerId}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={9}>
              <Input
                type="text"
                variant="outlined"
                name="email"
                label="External email"
                width="100%"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={formik.values.emailNotProvided}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={3} style={{ display: 'flex', alignItems: 'center', lineHeight: '56px' }}>
              <Box
                className={sl.checkboxWrapper}
                style={!!formik.errors.email ? { top: '-6px' } : {}}
              >
                <Checkbox
                  checked={formik.values.emailNotProvided}
                  label="Not provided"
                  onChange={() => {
                    if (!formik.values.emailNotProvided) {
                      formik.setFieldValue('email', '')
                    }
                    formik.setFieldValue('emailNotProvided', !formik.values.emailNotProvided)
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Input
                type="text"
                variant="outlined"
                name="phone"
                label="Phone"
                width="100%"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={formik.values.phoneNotProvided}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
            <Grid item xs={3} style={{ display: 'flex', alignItems: 'center', lineHeight: '56px' }}>
              <Box
                className={sl.checkboxWrapper}
                style={!!formik.errors.email ? { top: '-6px' } : {}}
              >
                <Checkbox
                  checked={formik.values.phoneNotProvided}
                  label="Not provided"
                  onChange={() => {
                    if (!formik.values.phoneNotProvided) {
                      formik.setFieldValue('phone', '')
                    }
                    formik.setFieldValue('phoneNotProvided', !formik.values.phoneNotProvided)
                  }}
                />
              </Box>
            </Grid>
            {partnerId && (
              <Grid item xs={12}>
                <span style={{ display: 'inline-block', lineHeight: '38px', marginRight: '12px' }}>
                  Status:
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      name="isEnabled"
                      checked={formik.values.isEnabled}
                      onChange={event => formik.setFieldValue('isEnabled', event.target.checked)}
                      onBlur={formik.handleBlur}
                    />
                  }
                  label="Enabled"
                />
                <span
                  style={{
                    color: 'rgba(0, 0, 0, 0.38)',
                    display: 'inline-block',
                    lineHeight: '38px'
                  }}
                >
                  (can be disabled if no active assigned partners)
                </span>
              </Grid>
            )}
            <Grid item xs={12}>
              <Input
                type="textarea"
                name="details"
                label="Additional details"
                style={{ width: '100%' }}
                rows={3}
                variant="outlined"
                value={formik.values.details}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue('details', event.currentTarget.value)
                }
                error={!!formik.errors.details}
                helperText={formik.errors.details as string}
              />
            </Grid>
            <Grid item xs={12} className={s.modalButtons}>
              <Button variant="outlined" onClick={onModalClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => formik.handleSubmit()}
                disabled={formik.isSubmitting}
              >
                Save
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  )
}

export default EditPartnerModal
